import { z } from "zod";
import { localStore } from "../../../lib/store";

/**
 * The uids of the accounts whose signup completion already fired before their
 * partner OAuth room was joined.
 *
 * An invite to a partner OAuth room completes the signup at the end of
 * onboarding, outside the room, because the OAuth round trip may never come
 * back (the reader closes the sheet, has no partner account, or the callback
 * fails). The OAuth callback consumes the uid to send no second completion for
 * that account; it fires one only for a member whose signup did not complete
 * this way. `localStorage`, because the callback can land in another session,
 * and a list, because two accounts can sign up on one device before either
 * callback returns.
 */
const oauthSignupCompletedUidsStore = localStore<string[]>(
  "oauth-signup-completed-uids",
  [],
  z.array(z.string())
);

/** The signup of `uid` has sent its completion, outside its OAuth room. */
export function markOAuthSignupCompleted(uid: string): void {
  oauthSignupCompletedUidsStore.set((uids) =>
    uids.includes(uid) ? uids : [...uids, uid]
  );
}

/**
 * Whether the signup of `uid` already completed. True only once: the uid is
 * removed, so a later OAuth join of the same account is a member's join.
 */
export function consumeOAuthSignupCompleted(uid: string): boolean {
  const uids = oauthSignupCompletedUidsStore.get();
  if (!uids.includes(uid)) return false;
  oauthSignupCompletedUidsStore.set(uids.filter((stored) => stored !== uid));
  return true;
}
